'use client';

import { ProductCarousel, ProductSummary } from '@fdc/__generated__/graphql';
import { SkipLink } from '@fdc/components/layout/navigation-header/skip-link/skip-link';
import { Carousel } from '@fdc/components/ui/carousel/carousel';
import { ViewItemListTileImpression } from '@fdc/components/ui/nz/product-grid/view-item-list-tile-impression';
import { SkipLinkTarget } from '@fdc/components/ui/nz/skip-link-target/skip-link-target';
import { ProductGrid } from '@fdc/components/ui/product-grid/product-grid';
import { ProductTileGhost } from '@fdc/components/ui/product-tile/product-tile-ghost';
import {
  requiresHeroPriceSpace,
  requiresProductTagSpace,
  tileItemRequiresHeroPriceSpace,
  tileItemRequiresProductTagSpace,
} from '@fdc/components/ui/product-tile/product-tile-space-utils';
import { ProductTileProps } from '@fdc/components/ui/product-tile/product-tile.types';
import { SideTile } from '@fdc/components/ui/side-tile/side-tile';
import {
  ISideTile,
  SideTileVariant,
} from '@fdc/components/ui/side-tile/side-tile-model';
import { CustomEventType } from '@fdc/constants/custom-event.constants';
import { NAVIGATION_CONSTANTS } from '@fdc/constants/navigation.constants';
import { trackAnalyticsEvent } from '@fdc/utils/analytics';
import { navigationUtils } from '@fdc/utils/navigation/navigation-utils';
import { useIntersectionObserver } from '@woolworthslimited/core-components-react/shared/hooks/useIntersectionObserver';
import { useWindowDimension } from '@woolworthslimited/core-components-react/shared/hooks/useWindowDimension';
import { isSmallDeviceOrGreater } from '@woolworthslimited/core-components-react/shared/services/breakpoint/breakpoint.service';
import Link from 'next/link';
import { Fragment, useId, useRef } from 'react';

import { ProductCarouselCartTileWrapper } from './product-carousel-cart-tile-wrapper';
import type {
  ProductCarouselOverrideProps,
  ProductCarouselProps,
} from './product-carousel-model';
import { ProductCarouselTile } from './product-carousel-tile';
import styles from './product-carousel.module.scss';

const SITE_ORIGIN = 'https://www.woolworths.co.nz';

const SIDE_PANEL_VARIANT_MAP: Record<string, SideTileVariant> = {
  Image: SideTileVariant.ImageOnly,
  'Image only': SideTileVariant.ImageOnly,
  'Image and text': SideTileVariant.ImageAndText,
  'Text only': SideTileVariant.TextOnly,
};

const PRODUCTS_LIMIT_FOR_CAROUSEL = 18;
const PRODUCTS_LIMIT_FOR_GRID = 12;

const mapSidePanelVariant = (variant: string): SideTileVariant =>
  SIDE_PANEL_VARIANT_MAP[variant] ?? SideTileVariant.TextOnly;

const CarouselProductTile: React.FC<ProductTileProps> = (props) => (
  <ProductCarouselTile {...props} />
);

export const ProductCarouselWithSidePanel: React.FC<
  ProductCarousel & ProductCarouselOverrideProps
> = ({
  heading,
  bodyText,
  showShopAll,
  browseCategory,
  productGroupId,
  sidePanel,
  variant = 'Grid',
  items = [],
  products = [],
  itemListId,
  itemListName,
  analyticsProps,
  isInsideComponent = false,
  viewItemListTracking,
  isLoading = false,
}) => {
  const anchorId = useId();
  const isGrid = variant === 'Grid';
  const productsLimit = isGrid
    ? PRODUCTS_LIMIT_FOR_GRID
    : PRODUCTS_LIMIT_FOR_CAROUSEL;
  const limitedItems = items.slice(0, productsLimit);
  const limitedProducts = products.slice(0, productsLimit);
  const reserveHeroPriceSpace =
    limitedProducts.length > 0
      ? limitedProducts.some(requiresHeroPriceSpace)
      : limitedItems.some(tileItemRequiresHeroPriceSpace);
  const reserveProductTagSpace =
    limitedProducts.length > 0
      ? limitedProducts.some(requiresProductTagSpace)
      : limitedItems.some(tileItemRequiresProductTagSpace);

  const productCarouselProps: ProductCarouselProps = {
    items: limitedItems,
    sideTile: sidePanel
      ? {
          heading: sidePanel.heading || '',
          description: sidePanel.bodyText || '',
          variant: mapSidePanelVariant(sidePanel.variant),
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: GQL schema returns string, needs enum validation
          colorVariant: sidePanel.scheme as ISideTile['colorVariant'],
          cta: sidePanel.cta
            ? {
                text: sidePanel.cta.label || '',
                aria: sidePanel.cta.ariaLabel || '',
                action: sidePanel.cta.link || '',
              }
            : undefined,
          imageUrl: sidePanel.image?.url || '',
          imageAlt: sidePanel.image?.altText || '',
        }
      : undefined,
    carouselTitle: heading,
    showShopAll,
    browseCategory,
    productGroupId,
  };

  const [width] = useWindowDimension(false);
  const isDesktop = isSmallDeviceOrGreater(width);

  const hasTriggeredVisibility = useRef(false);
  const ref = useIntersectionObserver<HTMLDivElement>(
    (entry) => {
      if (entry.isIntersecting && !hasTriggeredVisibility.current) {
        hasTriggeredVisibility.current = true;

        trackAnalyticsEvent(
          CustomEventType.PRODUCT_CAROUSEL_FIFTY_PERCENT_VIEW_EVENT,
          {
            ...productCarouselProps,
          }
        );
      }
    },
    { threshold: 0.5 }
  );

  const title = productCarouselProps.carouselTitle;
  const showShopAllLink = productCarouselProps.showShopAll;
  const shopAllUrl = productCarouselProps.browseCategory
    ? `${NAVIGATION_CONSTANTS.SHOP_BROWSE_PAGE}/${productCarouselProps.browseCategory}`
    : productCarouselProps.productGroupId
      ? `${NAVIGATION_CONSTANTS.PRODUCT_GROUP_PAGE}/${productCarouselProps.productGroupId}`
      : undefined;
  const sideTile = productCarouselProps.sideTile;
  let carouselShouldShowShopAll = false;
  if (!isGrid) {
    if (sideTile?.cta) {
      carouselShouldShowShopAll = !isDesktop;
    } else {
      carouselShouldShowShopAll = !!showShopAllLink;
    }
  }
  const shouldShowShopAll = isGrid || carouselShouldShowShopAll;

  const schemaItems =
    products?.length > 0
      ? limitedProducts.map((product, index) => ({
          position: index + 1,
          url: `${SITE_ORIGIN}${navigationUtils.getProductDetailsUrl(
            '/shop/product-details',
            product.sku,
            product.slug ?? ''
          )}`,
        }))
      : limitedItems.map((tile, index) => ({
          position: index + 1,
          url: `${SITE_ORIGIN}${navigationUtils.getProductDetailsUrl(
            tile.productDetailsPageBaseUrl,
            tile.product.productId,
            tile.product.slug ?? ''
          )}`,
        }));

  const productListJsonLd =
    schemaItems.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          ...(title ? { name: title } : {}),
          itemListOrder: 'https://schema.org/ItemListOrderAscending',
          numberOfItems: schemaItems.length,
          itemListElement: schemaItems.map((item) => ({
            '@type': 'ListItem',
            position: item.position,
            url: item.url,
          })),
        }
      : null;

  const getProductTileKey = (tile: ProductTileProps, i: number) =>
    tile.product.productId != null
      ? `${tile.product.productId}-${i}`
      : `carousel-item-${i}`;

  const renderCarouselProductTile = (
    tile: ProductTileProps,
    index: number,
    displayNarrowLayout?: boolean
  ) => {
    const productTile = (
      <CarouselProductTile
        {...tile}
        displayNarrowLayout={displayNarrowLayout}
        reserveHeroPriceSpace={
          tile.reserveHeroPriceSpace ?? reserveHeroPriceSpace
        }
        reserveProductTagSpace={
          tile.reserveProductTagSpace ?? reserveProductTagSpace
        }
      />
    );
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: refactor to remove type assertion
    const trackedProduct = viewItemListTracking?.products[index] as
      | ProductSummary
      | undefined;

    if (!viewItemListTracking || !trackedProduct?.sku) {
      return (
        <Fragment key={getProductTileKey(tile, index)}>{productTile}</Fragment>
      );
    }

    return (
      <ViewItemListTileImpression
        key={trackedProduct.sku}
        itemListId={viewItemListTracking.itemListId}
        sku={trackedProduct.sku}
        product={trackedProduct}
        listIndex={index}
      >
        {productTile}
      </ViewItemListTileImpression>
    );
  };

  const renderProductTileCartWrapper = (
    product: ProductSummary,
    index: number,
    displayNarrowLayout?: boolean
  ) => {
    const productTile = (
      <ProductCarouselCartTileWrapper
        product={product}
        index={index}
        itemListId={itemListId}
        itemListName={itemListName}
        displayNarrowLayout={displayNarrowLayout}
        analyticsProps={analyticsProps}
        reserveHeroPriceSpace={reserveHeroPriceSpace}
        reserveProductTagSpace={reserveProductTagSpace}
      />
    );
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: refactor to remove type assertion
    const trackedProduct = viewItemListTracking?.products[index] as
      | ProductSummary
      | undefined;

    if (!viewItemListTracking || !trackedProduct?.sku) {
      return (
        <Fragment
          key={
            product.sku != null
              ? `${product.sku}-${index}`
              : `carousel-item-${index}`
          }
        >
          {productTile}
        </Fragment>
      );
    }

    return (
      <ViewItemListTileImpression
        key={trackedProduct.sku}
        itemListId={viewItemListTracking.itemListId}
        sku={trackedProduct.sku}
        product={trackedProduct}
        listIndex={index}
      >
        {productTile}
      </ViewItemListTileImpression>
    );
  };

  const productTilesInNarrowLayout = limitedItems?.map(
    (tile: ProductTileProps, i) => renderCarouselProductTile(tile, i, isDesktop)
  );

  const productWrapperTilesInNarrowLayout = limitedProducts?.map(
    (product: ProductSummary, i) =>
      renderProductTileCartWrapper(product, i, isDesktop)
  );

  const renderHeader = () => {
    if (!title) {
      return null;
    }

    return (
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>{title}</h2>
          {bodyText && <p className={styles.bodyText}>{bodyText}</p>}
        </div>
        {shouldShowShopAll &&
          (shopAllUrl ? (
            <Link
              href={shopAllUrl}
              className={styles.shopAllLink}
              prefetch={false}
            >
              {'See All'}
            </Link>
          ) : null)}
      </div>
    );
  };

  if (!width) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={styles.productCarousel}>
        {renderHeader()}
        {!isGrid && (
          <div data-testid="carousel">
            <Carousel>
              {isDesktop && (
                <div className={styles.container}>
                  {Array.from({ length: 6 }, (_, i) => (
                    <ProductTileGhost key={i} displayNarrowLayout />
                  ))}
                </div>
              )}
              {!isDesktop &&
                Array.from({ length: 6 }, (_, i) => (
                  <div
                    key={`ghost-container-${i}`}
                    className={styles.container}
                  >
                    <ProductTileGhost displayNarrowLayout />
                  </div>
                ))}
            </Carousel>
          </div>
        )}
        {isGrid && (
          <div data-testid="product-grid">
            <ProductGrid>
              {Array.from({ length: 6 }, (_, i) => (
                <ProductTileGhost key={i} displayNarrowLayout />
              ))}
            </ProductGrid>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.productCarousel}>
      {productListJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(productListJsonLd),
          }}
        />
      )}
      {renderHeader()}

      {!isInsideComponent && (
        <SkipLink anchorId={anchorId} isSpaceRemoved label="Skip promo items" />
      )}

      {!isGrid && (
        <div data-testid="carousel">
          <Carousel>
            {isDesktop && (
              <div className={styles.container}>
                {sideTile && (
                  <>
                    {isInsideComponent && (
                      <SkipLink
                        anchorId={anchorId}
                        isInsideComponent
                        isSpaceRemoved
                        label="Skip promo items"
                      />
                    )}
                    <div ref={ref}>
                      <SideTile data={sideTile} />
                    </div>
                  </>
                )}
                {products?.length > 0
                  ? productWrapperTilesInNarrowLayout
                  : productTilesInNarrowLayout}
              </div>
            )}
            {!isDesktop &&
              (products?.length > 0
                ? limitedProducts.map((product: ProductSummary, i: number) => (
                    <div
                      key={
                        product.sku !== null && product.sku !== undefined
                          ? `${product.sku}-${i}`
                          : `carousel-item-${i}`
                      }
                      className={styles.container}
                    >
                      {renderProductTileCartWrapper(product, i, isDesktop)}
                    </div>
                  ))
                : limitedItems?.map((tile: ProductTileProps, i: number) => (
                    <div
                      key={getProductTileKey(tile, i)}
                      className={styles.container}
                    >
                      {renderCarouselProductTile(tile, i, isDesktop)}
                    </div>
                  )))}
          </Carousel>
        </div>
      )}

      {isGrid && (
        <div data-testid="product-grid">
          <ProductGrid>
            {products?.length > 0
              ? productWrapperTilesInNarrowLayout
              : productTilesInNarrowLayout}
          </ProductGrid>
        </div>
      )}

      <SkipLinkTarget anchorId={anchorId} />
    </div>
  );
};

export default ProductCarouselWithSidePanel;
